import ContentsToReact from '@/components/ContentsToReact/ContentsToReact';
import GenericModals from '@/components/GenericModals/GenericModals';
import Page from '@/components/Page/Page';
import { extractContents } from '@/helpers/extractContents';
import extractPageMetaFromRouteData from '@/helpers/extractPageMetaFromRouteData';
import { logger } from '@/helpers/logger';
import spliceHeaderFromContentItems from '@/helpers/spliceHeaderFromContentItems';
import { useInitialPageDataLayer } from '@/hooks/useInitialPageDataLayer/useInitialPageDataLayer';
import { PageModel } from '@/models/page';
import Headline from '@/uikit/components/Headline/Headline';
import {
  HeadlineAlign,
  HeadlineColor,
} from '@/uikit/components/Headline/consts';
import AccordionItem from '@uikit/components/AccordionItem/AccordionItem';
import Container from '@uikit/components/Container/Container';
import {
  ContainerChildrenSpace,
  ContainerSize,
} from '@uikit/components/Container/consts';
import EnercityFooter from '@uikit/components/EnercityFooter/EnercityFooter';
import Icon from '@uikit/components/Icon/Icon';
import Layout from '@uikit/components/Layout/Layout';
import PressHeader from '@uikit/components/Press/PressHeader/PressHeader';
import PressHr from '@uikit/components/Press/PressHr/PressHr';
import PressRelatedArticles from '@uikit/components/Press/PressRelatedArticles/PressRelatedArticles';
import PressContactList from '@uikit/components/PressContactList/PressContactList';
import RichText from '@uikit/components/RichText/RichText';
import Share from '@uikit/components/Share/Share';
import Spacer from '@uikit/components/Spacer/Spacer';
import Stack from '@uikit/components/Stack/Stack';
import PressArticleSection from '@uikit/composites/PressArticleSection/PressArticleSection';
import PressOnCallButton from '@uikit/composites/PressOnCallButton/PressOnCallButton';
import { CmsComponentsContext } from '../CmsComponentsContext';
import cmsComponents from './PressArticlePage.cmsComponents';
import type { PressArticlePageProps } from './interfaces';

const backButtonUrl = (pressType?: string): string => {
  switch (pressType) {
    case 'default':
      return '/presse/pressemitteilungen';
    case 'constructions':
      return '/presse/betrieb-und-baustellen';
    default:
      logger.warn(`Warning pressType isn't configured ${pressType}`);
      return '/presse';
  }
};

const PressArticlePage = (
  routeData: PageModel<PressArticlePageProps>
): JSX.Element => {
  const {
    contents,
    modals,
    path,
    props: {
      title,
      lastModified,
      teaserText,
      pressType,
      prettyTag = '',
      prettyTags,
      image,
      imageCaption,
      publicationDate,
      assetLink,
      assetCollectLink,
      teaserBullets,
      aboutEnercityTitle,
      aboutEnercityDescription,
      relatedArticles = [],
      pressContacts = [],
      mediaType,
    },
  } = routeData;
  const meta = extractPageMetaFromRouteData(routeData, {
    ogType: 'article',
  });

  const pressHeaderDate = publicationDate || lastModified || 'undefined';

  const {
    extractedContents: [additionalContents],
    restContents,
  } = extractContents(['additional-contents'], contents);

  const { items, header } = spliceHeaderFromContentItems(restContents);

  useInitialPageDataLayer([
    {
      event: 'pageView',
      ...(prettyTag ? { pressMainTopic: prettyTag } : {}),
      ...(prettyTags ? { pressSubTopic: prettyTags.join(', ') } : {}),
      ...(mediaType ? { pressMediaType: mediaType } : {}),
    },
  ]);

  return (
    <CmsComponentsContext.Provider value={cmsComponents}>
      <Layout>
        <Page meta={meta}>
          {header && <ContentsToReact contents={[header]} />}

          <PressArticleSection>
            <Container
              size={ContainerSize.TinyPlus}
              spaceBetweenChildren={ContainerChildrenSpace.Small}
            >
              <PressHeader
                date={pressHeaderDate}
                tag={prettyTag}
                tags={prettyTags}
                headline={title}
                asset={image}
                imageCaption={imageCaption}
                assetDownloadLink={assetLink}
                backButtonPath={backButtonUrl(pressType)}
                shareData={{
                  text: teaserText,
                  title: title,
                  url: `https://www.enercity.de${path}`,
                }}
              >
                {teaserBullets && <RichText>{teaserBullets}</RichText>}
              </PressHeader>

              <ContentsToReact contents={items} />

              {aboutEnercityTitle && aboutEnercityDescription && (
                <AccordionItem headline={aboutEnercityTitle}>
                  <RichText>{aboutEnercityDescription}</RichText>
                </AccordionItem>
              )}

              <div className={'PressArticle-module-download'}>
                <div className={'PressArticle-module-download-all'}>
                  {assetCollectLink && (
                    <>
                      <Icon variant={'action/camera'} size="iconSize24" />
                      <a
                        href={assetCollectLink}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Alle Bilder herunterladen
                      </a>
                    </>
                  )}
                </div>

                <Share
                  text={teaserText}
                  title={title}
                  url={`https://www.enercity.de${path}`}
                />
              </div>

              <PressHr />

              <Stack gap={1}>
                <Headline
                  headlineColor={HeadlineColor.Dark}
                  level={3}
                  subText={`
                Wir sind gerne für Sie da. Bitte haben Sie jedoch Verständnis, dass wir an dieser Stelle ausschließlich Anfragen von
                Journalisten und weiteren Medienschaffenden beantworten können.
                `}
                  tag="div"
                  align={HeadlineAlign.Left}
                  className="PressArticle-module-contact-headline"
                >
                  Pressekontakt
                </Headline>

                {!!pressContacts.length && (
                  <>
                    <Stack gap={4}>
                      <PressContactList items={pressContacts} />

                      <Headline
                        headlineColor={HeadlineColor.Dark}
                        level={4}
                        align={HeadlineAlign.Left}
                        tag="div"
                      >
                        Unser Service für Journalist:innen: die
                        Presse-Rufbereitschaft.
                      </Headline>
                    </Stack>

                    <PressOnCallButton />

                    <Spacer defaultSize={2} />
                  </>
                )}
              </Stack>

              {prettyTag && !!relatedArticles.length && (
                <>
                  <PressHr />
                  <PressRelatedArticles
                    tag={prettyTag}
                    items={relatedArticles}
                  />
                </>
              )}
            </Container>
          </PressArticleSection>

          {additionalContents && (
            <ContentsToReact contents={additionalContents} />
          )}
        </Page>

        <EnercityFooter />
      </Layout>
      <GenericModals modals={modals} />
    </CmsComponentsContext.Provider>
  );
};

export default PressArticlePage;
